class ProductBuyMoreSave {
  constructor(element) {
    this.element = element;

    this.requiredQuantity =
      parseInt(this.element.dataset.requiredQuantity, 10) || 1;

    this.bundlePrice =
      parseFloat(this.element.dataset.bundlePrice) || 0;

    this.comparePrice =
      parseFloat(this.element.dataset.comparePrice) || 0;

    this.moneyFormat =
      this.element.dataset.moneyFormat || '${{amount}}';

    this.configElement =
      this.element.querySelector('[data-buy-more-save-config]');

    this.config = this.getConfig();

    this.variantRows = [
      ...this.element.querySelectorAll('[data-buy-more-save-variant]')
    ];

    this.submitButton =
      this.element.querySelector('[data-buy-more-save-submit]');

    this.submitText =
      this.element.querySelector('[data-buy-more-save-submit-text]');

    this.bundlePriceElement =
      this.element.querySelector('[data-buy-more-save-bundle-price]');

    this.comparePriceElement =
      this.element.querySelector('[data-buy-more-save-compare-price]');

    this.init();
  }

  getConfig() {
    if (!this.configElement) return {};

    try {
      return JSON.parse(this.configElement.textContent);
    } catch (error) {
      console.error('Buy More & Save: Invalid config JSON.', error);
      return {};
    }
  }

  init() {
    this.variantRows.forEach((row) => {
      row.dataset.quantity = '0';

      const addButton =
        row.querySelector('[data-buy-more-save-add]');

      const minusButton =
        row.querySelector('[data-buy-more-save-minus]');

      const plusButton =
        row.querySelector('[data-buy-more-save-plus]');

      addButton?.addEventListener('click', () => {
        this.addVariant(row);
      });

      minusButton?.addEventListener('click', () => {
        this.decreaseVariant(row);
      });

      plusButton?.addEventListener('click', () => {
        this.increaseVariant(row);
      });
    });

    this.submitButton?.addEventListener('click', () => {
      this.addToCart();
    });

    this.update();
  }

  addVariant(row) {
    if (this.getTotalQuantity() >= this.requiredQuantity) return;

    row.dataset.quantity = '1';

    this.updateRow(row);
    this.update();
  }

  increaseVariant(row) {
    if (this.getTotalQuantity() >= this.requiredQuantity) return;

    const currentQuantity =
      parseInt(row.dataset.quantity, 10) || 0;

    row.dataset.quantity =
      String(currentQuantity + 1);

    this.updateRow(row);
    this.update();
  }

  decreaseVariant(row) {
    const currentQuantity =
      parseInt(row.dataset.quantity, 10) || 0;

    if (currentQuantity <= 0) return;

    row.dataset.quantity =
      String(currentQuantity - 1);

    this.updateRow(row);
    this.update();
  }

  updateRow(row) {
    const quantity =
      parseInt(row.dataset.quantity, 10) || 0;

    const addButton =
      row.querySelector('[data-buy-more-save-add]');

    const quantitySelector =
      row.querySelector('[data-buy-more-save-quantity]');

    const quantityValue =
      row.querySelector('[data-buy-more-save-quantity-value]');

    if (quantity > 0) {
      if (addButton) {
        addButton.hidden = true;
      }

      if (quantitySelector) {
        quantitySelector.hidden = false;
      }

      if (quantityValue) {
        quantityValue.textContent = quantity;
      }
    } else {
      if (addButton) {
        addButton.hidden = false;
      }

      if (quantitySelector) {
        quantitySelector.hidden = true;
      }

      if (quantityValue) {
        quantityValue.textContent = '1';
      }
    }
  }

  update() {
    const totalQuantity =
      this.getTotalQuantity();

    const complete =
      totalQuantity === this.requiredQuantity;

    this.updateButtons(complete);
    this.updateSubmitButton(complete);
    this.updatePricing();
  }

  updateButtons(complete) {
    this.variantRows.forEach((row) => {
      const quantity =
        parseInt(row.dataset.quantity, 10) || 0;

      const addButton =
        row.querySelector('[data-buy-more-save-add]');

      const plusButton =
        row.querySelector('[data-buy-more-save-plus]');

      const minusButton =
        row.querySelector('[data-buy-more-save-minus]');

      const available =
        row.dataset.variantAvailable === 'true';

      if (addButton) {
        addButton.disabled =
          !available || complete;
      }

      if (plusButton) {
        plusButton.disabled =
          !available || complete;
      }

      if (minusButton) {
        minusButton.disabled =
          quantity <= 0;
      }
    });
  }

  updateSubmitButton(complete) {
    if (!this.submitButton) return;

    this.submitButton.disabled = !complete;

    if (!this.submitText) return;

    if (complete) {
      this.submitText.textContent =
        this.config.cartButtonText || 'Add to cart';
    } else {
      this.submitText.textContent =
        this.config.cartButtonIncompleteText ||
        'Select more items';
    }
  }

  updatePricing() {
    if (
      this.bundlePriceElement &&
      this.bundlePrice > 0
    ) {
      this.bundlePriceElement.textContent =
        this.formatMoney(
          Math.round(this.bundlePrice * 100)
        );
    }

    if (
      this.comparePriceElement &&
      this.comparePrice > 0
    ) {
      this.comparePriceElement.textContent =
        this.formatMoney(
          Math.round(this.comparePrice * 100)
        );
    }
  }

  getTotalQuantity() {
    return this.variantRows.reduce(
      (total, row) => {
        return (
          total +
          (parseInt(row.dataset.quantity, 10) || 0)
        );
      },
      0
    );
  }

  getSelectedItems() {
    return this.variantRows
      .map((row) => {
        const quantity =
          parseInt(row.dataset.quantity, 10) || 0;

        if (quantity <= 0) return null;

        return {
          id: Number(row.dataset.variantId),
          quantity
        };
      })
      .filter(Boolean);
  }

  formatMoney(cents) {
    const value = cents / 100;

    const amount =
      value.toFixed(2);

    const amountNoDecimals =
      Math.round(value).toString();

    const amountWithCommaSeparator =
      amount.replace('.', ',');

    const amountNoDecimalsWithCommaSeparator =
      amountNoDecimals;

    const amountWithApostropheSeparator =
      amount;

    let formatted =
      this.moneyFormat;

    formatted = formatted
      .replace(
        /\{\{\s*amount\s*\}\}/g,
        amount
      )
      .replace(
        /\{\{\s*amount_no_decimals\s*\}\}/g,
        amountNoDecimals
      )
      .replace(
        /\{\{\s*amount_with_comma_separator\s*\}\}/g,
        amountWithCommaSeparator
      )
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        amountNoDecimalsWithCommaSeparator
      )
      .replace(
        /\{\{\s*amount_with_apostrophe_separator\s*\}\}/g,
        amountWithApostropheSeparator
      );

    return formatted;
  }

  async addToCart() {
    const items =
      this.getSelectedItems();

    if (
      this.getTotalQuantity() !==
      this.requiredQuantity
    ) {
      return;
    }

    if (!items.length) return;

    this.submitButton.disabled = true;
    this.submitButton.setAttribute('aria-busy', 'true');
    this.element.classList.add('is-loading');

    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:start', {
        bubbles: true
      })
    );

    try {
      const sectionsToBundle = [];

      document.documentElement.dispatchEvent(
        new CustomEvent(
          'cart:prepare-bundled-sections',
          {
            bubbles: true,
            detail: {
              sections: sectionsToBundle
            }
          }
        )
      );

      const response = await fetch(
        `${Shopify.routes.root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname
          })
        }
      );

      const responseJson =
        await response.json();

      if (!response.ok) {
        throw new Error(
          responseJson.description ||
          responseJson.message ||
          'Unable to add items to cart.'
        );
      }

      const cartContent = await (
        await fetch(
          `${Shopify.routes.root}cart.js`
        )
      ).json();

      cartContent.sections =
        responseJson.sections;

      const addedItems =
        responseJson.hasOwnProperty('items')
          ? responseJson.items
          : [responseJson];

      this.element.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: addedItems,
            cart: cartContent,
            onSuccessDo: null
          }
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            onSuccessDo: null,
            cart: cartContent
          }
        })
      );
    } catch (error) {
      console.error(
        'Buy More & Save: Add to cart failed.',
        error
      );

      document.dispatchEvent(
        new CustomEvent('cart:refresh')
      );
    } finally {
      this.submitButton.removeAttribute('aria-busy');
      this.element.classList.remove('is-loading');

      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:end', {
          bubbles: true
        })
      );

      this.update();
    }
  }
}

function initProductBuyMoreSave(scope = document) {
  scope
    .querySelectorAll('[data-buy-more-save]')
    .forEach((element) => {
      if (
        element.dataset.initialized === 'true'
      ) {
        return;
      }

      element.dataset.initialized = 'true';

      new ProductBuyMoreSave(element);
    });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initProductBuyMoreSave();
    }
  );
} else {
  initProductBuyMoreSave();
}

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initProductBuyMoreSave(event.target);
  }
);